import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Spinner,
  ExpandableSection,
  Table,
  Link,
  Grid,
  Button,
  TextContent,
} from '@amzn/awsui-components-core';
import { UserRoles, CollectionAudience } from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import { withRouter } from 'react-router';
import { isUUID } from 'class-validator';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import NotFound from '../common/NotFound';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  Collection,
  CollectionEpic,
  CollectionStory,
  Owner,
} from '@amzn/awscat-pattern-library-service-model';
import {
  displayOwners,
  CollectionEpicCsvFormat,
  generateEpicCsvData,
  DOWNLOAD_CSV_BUTTON_TEXT,
} from './utils';
import CsvDownloadButton from '../comment/CsvDownloadButton';
import { AppContext } from '../../contexts/AppContext';
import RouterPaths, { EDIT_COLLECTION_PATH_PREFIX } from '../../common/RouterPaths';
import BrowserHistory from '../utils/BrowserHistory';
import ContentEngagementInfo from '../content-engagement/ContentEngagementInfo';
import FavoriteCollection from './FavoriteCollection';
import { convertEntities } from '@amzn/awscat-editor';
import { ContentFeedback, FeedbackRatingSystem, FeedbackSource } from '../feedback-service';

export interface Props {
  match: any;
}

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const CollectionViewer = (props: Props) => {
  const [collection, setCollection] = useState<Collection>();
  const [isLoadingCollection, setIsLoadingCollection] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const { entities } = appContext;

  const { collectionId } = props.match.params;

  useEffect(() => {
    const loadCollection = async () => {
      if (!isUUID(collectionId)) {
        return;
      }
      setIsLoadingCollection(true);
      const response = await mplServiceClient.getCollection(collectionId);
      setCollection(response.collection);
      document.title = response.collection.title;
      setIsLoadingCollection(false);
    };
    loadCollection();

    return () => {
      document.title = constants.DEFAULT_TAB_TITLE;
    };
  }, [collectionId]);

  const renderByLine = () => {
    // @ts-expect-error TS2339 - strict migration
    const { createdAt } = collection;
    // @ts-expect-error TS18048 - strict migration
    const ownersInfo = displayOwners(collection.owners as Owner[]);
    return `${ownersInfo} on ${createdAt.toLocaleString(DateTime.DATE_MED)}`;
  };

  const renderDescription = () => {
    // @ts-expect-error TS2339 - strict migration
    const { description } = collection;
    return (
      <div className="collection-viewer-desc-container">
        <div className="collection-viewer-section-title">
          <Box color="inherit" variant="h3" padding={{ top: 'm', bottom: 'm' }}>
            {constants.COLLECTION_VIEWER_DESCRIPTION}
          </Box>
        </div>
        <Box variant="p">{description}</Box>
      </div>
    );
  };

  if (!isUUID(props.match.params.collectionId)) {
    return <NotFound />;
  }

  if (isLoadingCollection || !collection) {
    return <Spinner />;
  }

  const renderEpics = () => {
    return (
      <>
        <div className="collection-viewer-section-title collection-viewer-epics">
          <Box color="inherit" variant="h3" padding={{ top: 'm', bottom: 's' }}>
            {constants.COLLECTION_VIEWER_EPICS}
          </Box>
          <Box float="right" padding={{ top: 'xs' }}>
            <CsvDownloadButton
              // @ts-expect-error TS2345 - strict migration
              data={generateEpicCsvData(collection.epics)}
              format={CollectionEpicCsvFormat}
              filename={`${collection.title}-epics.csv`}
              buttonText={DOWNLOAD_CSV_BUTTON_TEXT}
            />
          </Box>
        </div>
        {/* @ts-expect-error TS18048 - strict migration */}
        {collection.epics.map((epic, index) => {
          return (
            <div key={epic.title} className="collection-viewer-epic-container">
              {renderEpic(epic)}
            </div>
          );
        })}
      </>
    );
  };

  const renderAttachmentAndRepo = (story: CollectionStory) => {
    const { repos, attachments } = story;
    return (
      <>
        {repos?.length ? (
          <>
            <Box color="text-body-secondary">{constants.COLLECTION_VIEWER_REPOSITORY}</Box>
            <TextContent>
              {repos.map((repo, index) => {
                return (
                  <li key={repo.name}>
                    <Link external target="_blank" href={repo.link}>
                      {repo.name}
                    </Link>
                  </li>
                );
              })}
            </TextContent>
            <Box padding={{ bottom: 's' }} />
          </>
        ) : null}
        {attachments?.length ? (
          <>
            <Box color="text-body-secondary">{constants.COLLECTION_VIEWER_ATTACHMENTS}</Box>
            <TextContent>
              {attachments.map((attachment, index) => {
                return (
                  <li key={attachment.name}>
                    <Link external target="_blank" href={attachment.link}>
                      {decodeURIComponent(attachment.name)}
                    </Link>
                  </li>
                );
              })}
            </TextContent>
          </>
        ) : null}
      </>
    );
  };

  const columnDefinitions = [
    {
      id: constants.COLLECTION_VIEWER_STORY_TITLE_COL,
      header: constants.COLLECTION_VIEWER_STORY_TITLE_COL,
      // @ts-expect-error TS7006 - strict migration
      cell: (story) => <Box fontWeight="bold">{story.title}</Box>,
      width: '24%',
    },
    {
      id: constants.COLLECTION_VIEWER_STORY_DESC_COL,
      header: constants.COLLECTION_VIEWER_STORY_DESC_COL,
      // @ts-expect-error TS7006 - strict migration
      cell: (story) => story.description,
      width: '29%',
    },
    {
      id: constants.COLLECTION_VIEWER_GUIDANCE_COL,
      header: constants.COLLECTION_VIEWER_GUIDANCE_COL,
      // @ts-expect-error TS7006 - strict migration
      cell: (story) => (
        <>
          <Box>{story.contentType}</Box>
          <TextContent>
            <li>
              <Link
                external
                target="_blank"
                href={getContentPath({ contentId: story.publicationId, mode: 'publication' })}
              >
                {convertEntities(story.publicationName, entities)}
              </Link>
            </li>
          </TextContent>
        </>
      ),
      width: '23%',
    },
    {
      id: constants.COLLECTION_VIEWER_ATTACHMENT_AND_REPO_COL,
      header: constants.COLLECTION_VIEWER_ATTACHMENT_AND_REPO_COL,
      // @ts-expect-error TS7006 - strict migration
      cell: (story) => renderAttachmentAndRepo(story),
    },
  ];

  const renderEpic = (epic: CollectionEpic) => {
    return (
      <ExpandableSection
        headerDescription={epic.description}
        variant="container"
        defaultExpanded
        disableContentPaddings
        headerText={epic.title}
      >
        <Table
          columnDefinitions={columnDefinitions}
          items={epic.stories}
          wrapLines
          data-collection-viewer
        />
      </ExpandableSection>
    );
  };

  const redirectToEdit = () => {
    BrowserHistory.push(`${EDIT_COLLECTION_PATH_PREFIX}/${collection.collectionId}`);
  };

  const renderCollectionTitle = () => {
    // @ts-expect-error TS2339 - strict migration
    const { userId, roles } = appContext?.authnInfo?.userProfile;
    const { owners, locked, lockedBy, collectionId } = collection;
    const showEditButton =
      roles?.has(UserRoles.Admin) || owners?.some((owner) => owner.userId === userId);
    const collectionTitle = <Box variant="h2">{collection.title}</Box>;
    if (!showEditButton) {
      return collectionTitle;
    }
    return (
      <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
        {collectionTitle}
        <Box float="right">
          <Button iconName="edit" disabled={locked && lockedBy !== userId} onClick={redirectToEdit}>
            Edit
          </Button>
        </Box>
      </Grid>
    );
  };

  const renderVisibility = () => {
    const visibility =
      collection.audience === CollectionAudience.PRIVATE
        ? constants.MY_COLLECTION_AUDIENCE_LABEL_ONLY_OWNER
        : constants.MY_COLLECTION_AUDIENCE_LABEL_INTERNAL;
    return (
      <Box>
        <Box fontWeight="bold">Collection visibility</Box>
        {visibility}
      </Box>
    );
  };

  const renderLikeDislike = () => {
    return (
      <div>
        <Box fontWeight="bold">Rate this collection</Box>
        <ContentFeedback
          contentId={collection.collectionId}
          ratingSystem={FeedbackRatingSystem.LikeDislike}
          feedbackSource={FeedbackSource.Collection}
        />
      </div>
    );
  };

  return (
    <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
      <div className={'overflow-wrap-break-word'}>
        {renderCollectionTitle()}
        {renderByLine()}
        {renderDescription()}
        {renderEpics()}
      </div>
      <div className="collection-viewer-right-column-container">
        <FavoriteCollection collectionId={collection.collectionId} />
        <hr className="collection-viewer-hr" />
        {renderVisibility()}
        <hr className="collection-viewer-hr" />
        {renderLikeDislike()}
        <hr className="collection-viewer-hr" />
        <Box fontWeight="bold">Used in an engagement?</Box>
        <div>
          <ContentEngagementInfo contentId={collection?.collectionId} isCollection />
        </div>
      </div>
    </Grid>
  );
};

export default withRouter(CollectionViewer);
